
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertCircle, Loader2, InfoIcon } from "lucide-react";
import { verifyCharterSignatory } from "@/lib/supabase/charter";
import { useToast } from "@/components/ui/use-toast";

const CharteVerification = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const id = searchParams.get("id");
  const token = searchParams.get("token");

  const [verificationStatus, setVerificationStatus] = useState<
    "pending" | "success" | "success-with-warning" | "failed"
  >("pending");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [verificationDetails, setVerificationDetails] = useState<any>(null);

  useEffect(() => {
    const verifySignature = async () => {
      if (!id || !token) {
        setVerificationStatus("failed");
        setErrorMessage("Lien de vérification incomplet. Paramètres manquants.");
        return;
      }

      try {
        console.log(`[DEBUG] Tentative de vérification pour id=${id}, token=${token}`);
        const result = await verifyCharterSignatory(id, token);
        console.log(`[DEBUG] Résultat de vérification:`, result);
        setVerificationDetails(result);
        
        if (result.emailError) {
          setVerificationStatus("success-with-warning");
          toast({
            title: "Signature confirmée mais email non envoyé",
            description: "Votre signature a été confirmée, mais nous n'avons pas pu vous envoyer l'email de confirmation. Vous pouvez continuer à utiliser le site normalement.",
            variant: "default",
          });
        } else {
          setVerificationStatus("success");
          toast({
            title: "Signature confirmée",
            description: "Votre signature de la Charte Républicaine a été confirmée avec succès.",
          });
        }
        
        // Affichage du statut de la vérification d'email
        console.log("[DEBUG] Statut de vérification email:", result.emailVerified);
        
        // Redirection avec légère attente pour montrer le message de succès
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 5000); // 5 secondes pour laisser le temps de lire le message
      } catch (error) {
        console.error("[DEBUG] Erreur lors de la vérification:", error);
        setVerificationStatus("failed");
        setErrorMessage(error instanceof Error ? error.message : "Le lien est invalide ou a expiré.");
        toast({
          title: "Échec de la vérification",
          description: error instanceof Error ? error.message : "Le lien est invalide ou a expiré.",
          variant: "destructive",
        });
      }
    };

    verifySignature();
  }, [id, token, toast, navigate]);

  return (
    <div className="min-h-screen py-12 bg-gray-50">
      <div className="container max-w-md mx-auto">
        <Card className="border-2">
          <CardContent className="pt-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-6">Vérification de signature</h1>

              {verificationStatus === "pending" && (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-16 w-16 text-primary animate-spin" />
                  <p className="text-lg">Vérification en cours...</p>
                </div>
              )}

              {verificationStatus === "success" && (
                <div className="flex flex-col items-center gap-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-green-700">Signature confirmée avec succès !</p>
                    <p className="text-gray-600">Merci d'avoir signé la Charte Républicaine.</p>
                    {verificationDetails && (
                      <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-sm">
                        <p>Votre numéro de signature: <span className="font-semibold">#{verificationDetails.signatureNumber}</span></p>
                        <p>Vous êtes parmi les <span className="font-semibold">{verificationDetails.signatureCount}</span> signataires.</p>
                        <p className="mt-1 text-xs">Statut: <span className={verificationDetails.emailVerified ? "text-green-600" : "text-amber-600"}>
                          {verificationDetails.emailVerified ? "Vérifié" : "En cours de vérification"}
                        </span></p>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm">Redirection vers la page d'accueil...</p>
                  </div>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate("/", { replace: true })}
                  >
                    Aller à la page d'accueil
                  </Button>
                </div>
              )}

              {verificationStatus === "success-with-warning" && (
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-14 w-14 text-green-500" />
                    <InfoIcon className="h-9 w-9 text-amber-500" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-green-700">Signature confirmée avec succès !</p>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 my-3">
                      <p className="text-amber-800 text-sm">
                        Votre email a été vérifié, mais nous n'avons pas pu envoyer l'email de confirmation. 
                        Votre signature est bien enregistrée, vous pouvez continuer à utiliser le site normalement.
                      </p>
                    </div>
                    <p className="text-gray-600">Merci d'avoir signé la Charte Républicaine.</p>
                    {verificationDetails && (
                      <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-sm">
                        <p>Votre numéro de signature: <span className="font-semibold">#{verificationDetails.signatureNumber}</span></p>
                        <p>Vous êtes parmi les <span className="font-semibold">{verificationDetails.signatureCount}</span> signataires.</p>
                        <p className="mt-1 text-xs">Statut: <span className={verificationDetails.emailVerified ? "text-green-600" : "text-amber-600"}>
                          {verificationDetails.emailVerified ? "Vérifié" : "En cours de vérification"}
                        </span></p>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm">Redirection vers la page d'accueil...</p>
                  </div>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate("/", { replace: true })}
                  >
                    Aller à la page d'accueil
                  </Button>
                </div>
              )}

              {verificationStatus === "failed" && (
                <div className="flex flex-col items-center gap-4">
                  <AlertCircle className="h-16 w-16 text-red-500" />
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-red-700">Échec de la vérification</p>
                    <p className="text-gray-600">{errorMessage || "Le lien est invalide ou a expiré."}</p>
                    <p className="text-gray-600 text-sm">
                      Si vous n'avez pas encore signé la charte, vous pouvez le faire sur la page d'accueil.
                    </p>
                  </div>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate("/charte-republicaine", { replace: true })}
                  >
                    Signer la charte
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CharteVerification;
